import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFeatureCatalog } from '../../../api/plans.api';
import type { AdminPlan, PlanFeatureDefinition } from '../../../types/plan.types';

import { PLAN_CURRENCIES } from '../../../lib/planCurrency.constants';

interface PlanFormModalProps {
  isOpen: boolean;
  editingPlan: AdminPlan | null;
  saving: boolean;
  submitError: string;
  onClose: () => void;
  onCreate: (payload: {
    title: string;
    description: string;
    days: string;
    price: string;
    billing_cycle?: 'monthly' | 'yearly' | 'both';
    monthly_price?: string;
    yearly_price?: string;
    yearly_discount_percent?: string;
    currency?: string;
    features?: Record<string, string | boolean | number>;
  }) => Promise<void>;
  onUpdate: (
    id: number,
    payload: {
      title: string;
      description: string;
      days: string;
      price: string;
      is_active: boolean;
      billing_cycle?: 'monthly' | 'yearly' | 'both';
      monthly_price?: string;
      yearly_price?: string;
      yearly_discount_percent?: string;
      currency?: string;
      features?: Record<string, string | boolean | number>;
    },
  ) => Promise<void>;
}

const GROUP_LABELS: Record<string, string> = {
  core: 'الأساسيات',
  catalog: 'المنتجات',
  operations: 'التشغيل',
  sales: 'المبيعات والإيجار',
  purchasing: 'المشتريات',
  finance: 'المالية',
  analytics: 'التقارير والمحاسبة',
  intelligence: 'الذكاء',
  automation: 'المساعد الذكي والأتمتة',
  digital: 'الرقمي',
  limits: 'حدود الباقة',
};

const BILLING_CYCLES = [
  { value: 'monthly', label: 'monthly' },
  { value: 'yearly', label: 'yearly' },
  { value: 'both', label: 'both' },
];

const emptyForm = () => ({
  title: '',
  description: '',
  days: '365',
  price: '',
  billing_cycle: 'both' as 'monthly' | 'yearly' | 'both',
  monthly_price: '',
  yearly_price: '',
  yearly_discount_percent: '',
  currency: 'EGP',
  is_active: true,
  features: {} as Record<string, string>,
});

export default function PlanFormModal({
  isOpen,
  editingPlan,
  saving,
  submitError,
  onClose,
  onCreate,
  onUpdate,
}: PlanFormModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [catalog, setCatalog] = useState<PlanFeatureDefinition[]>([]);
  const [catalogError, setCatalogError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    void fetchFeatureCatalog().then((result) => {
      if (result.ok) {
        setCatalog(result.features);
        setCatalogError('');
      } else {
        setCatalogError(result.message);
      }
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (editingPlan) {
      const featureMap = editingPlan.feature_map ?? {};
      const features: Record<string, string> = {};
      for (const item of catalog) {
        if (item.type === 'integer') {
          features[item.key] = '0';
        }
      }
      for (const item of editingPlan.features ?? []) {
        features[item.feature_key] = item.feature_value;
      }
      setForm({
        title: editingPlan.title || editingPlan.name,
        description: editingPlan.description ?? '',
        days: String(editingPlan.days ?? editingPlan.duration_days ?? 365),
        price: String(editingPlan.price),
        billing_cycle: (editingPlan.billing_cycle as 'monthly' | 'yearly' | 'both') ?? 'both',
        monthly_price: editingPlan.monthly_price ?? '',
        yearly_price: editingPlan.yearly_price ?? '',
        yearly_discount_percent: editingPlan.yearly_discount_percent ?? '',
        currency: editingPlan.currency ?? 'EGP',
        is_active: editingPlan.is_active,
        features: { ...features, ...featureMap },
      });
    } else {
      const features: Record<string, string> = {};
      for (const item of catalog) {
        if (item.type === 'integer') {
          features[item.key] = '0';
        }
      }
      setForm({ ...emptyForm(), features });
    }
  }, [editingPlan, isOpen, catalog]);

  const groupedFeatures = useMemo(() => {
    const groups = new Map<string, PlanFeatureDefinition[]>();
    const order = ['limits', 'core', 'catalog', 'operations', 'sales', 'purchasing', 'finance', 'analytics', 'intelligence', 'automation', 'digital'];
    for (const item of catalog) {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    }
    const ordered = new Map<string, PlanFeatureDefinition[]>();
    for (const key of order) {
      if (groups.has(key)) {
        ordered.set(key, groups.get(key)!);
        groups.delete(key);
      }
    }
    for (const [key, list] of groups.entries()) {
      ordered.set(key, list);
    }
    return ordered;
  }, [catalog]);

  if (!isOpen) return null;

  const setFeature = (key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: value },
    }));
  };

  const toggleBooleanFeature = (key: string) => {
    const current = form.features[key];
    const enabled = ['true', '1', 'yes', 'enabled'].includes(String(current ?? '').toLowerCase());
    setFeature(key, enabled ? 'false' : 'true');
  };

  const handleSubmit = async () => {
    const title = form.title.trim();
    const description = form.description.trim();
    const days = form.days.trim();
    const price = form.price.trim();
    if (!title || !description || !days || !price) return;

    const payload = {
      title,
      description,
      days,
      price,
      billing_cycle: form.billing_cycle,
      monthly_price: form.monthly_price || undefined,
      yearly_price: form.yearly_price || undefined,
      yearly_discount_percent: form.yearly_discount_percent || undefined,
      currency: form.currency,
      features: form.features,
    };

    if (editingPlan) {
      await onUpdate(editingPlan.id, { ...payload, is_active: form.is_active });
    } else {
      await onCreate(payload);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">
            {editingPlan ? t('plans.form.title_edit') : t('plans.form.title_add')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {submitError ? (
            <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{submitError}</p>
          ) : null}
          {catalogError ? (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{catalogError}</p>
          ) : null}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.title_label')}</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder={t('plans.form.title_placeholder')}
                disabled={saving}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.description')}</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder={t('plans.form.description_placeholder')}
                rows={3}
                disabled={saving}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all resize-y min-h-[80px] disabled:opacity-60"
              />
            </div>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.currency')}</label>
            <select
              value={form.currency}
              onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
              disabled={saving}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60"
            >
              {PLAN_CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Billing Cycle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.billing_cycle')}</label>
            <div className="flex gap-2">
              {BILLING_CYCLES.map((bc) => (
                <button
                  key={bc.value}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, billing_cycle: bc.value as typeof p.billing_cycle }))}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    form.billing_cycle === bc.value
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } disabled:opacity-60`}
                >
                  {t(`plans.${bc.label}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing + subscription days */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.monthly_price')}</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.monthly_price}
                onChange={(e) => setForm((p) => ({ ...p, monthly_price: e.target.value }))}
                placeholder="100"
                disabled={saving}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.yearly_price')}</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.yearly_price}
                onChange={(e) => setForm((p) => ({ ...p, yearly_price: e.target.value }))}
                placeholder="1000"
                disabled={saving}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.yearly_discount')}</label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.yearly_discount_percent}
                  onChange={(e) => setForm((p) => ({ ...p, yearly_discount_percent: e.target.value }))}
                  placeholder="15"
                  disabled={saving}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60 pe-8"
                />
                <span className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.days')}</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.days}
                onChange={(e) => setForm((p) => ({ ...p, days: e.target.value }))}
                placeholder={t('plans.form.days_placeholder')}
                disabled={saving}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60"
              />
              <p className="text-xs text-gray-400 mt-1">{t('plans.form.days_help')}</p>
            </div>
          </div>

          {/* Legacy base price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('plans.form.price')} ({t('plans.form.base_price_legacy')})</label>
            <input
              type="text"
              inputMode="decimal"
              value={form.price}
              onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
              placeholder={t('plans.form.price_placeholder')}
              disabled={saving}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all disabled:opacity-60"
            />
            <p className="text-xs text-gray-400 mt-1">{t('plans.form.base_price_help')}</p>
          </div>

          {/* Features + real limits from catalog */}
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-3">{t('plans.form.features_label')}</p>
            <div className="space-y-4">
              {[...groupedFeatures.entries()].map(([group, items]) => (
                <div key={group} className="rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    {GROUP_LABELS[group] ?? group}
                  </div>
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {items.map((feature) => {
                      if (feature.type === 'integer') {
                        return (
                          <div key={feature.key}>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{feature.label}</label>
                            <input
                              type="number"
                              min={0}
                              value={form.features[feature.key] ?? '0'}
                              onChange={(e) => setFeature(feature.key, e.target.value)}
                              disabled={saving}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">{feature.description}</p>
                          </div>
                        );
                      }

                      const enabled = ['true', '1', 'yes', 'enabled'].includes(
                        String(form.features[feature.key] ?? '').toLowerCase(),
                      );

                      return (
                        <label
                          key={feature.key}
                          className="flex items-start gap-3 rounded-lg border border-gray-100 px-3 py-2 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => toggleBooleanFeature(feature.key)}
                            disabled={saving}
                            className="mt-0.5 w-4 h-4 accent-teal-600"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-700">{feature.label}</p>
                            <p className="text-[11px] text-gray-400">{feature.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {editingPlan ? (
            <label className="flex items-center gap-3 rounded-lg border border-gray-100 px-4 py-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                disabled={saving}
                className="w-4 h-4 accent-teal-600"
              />
              <span className="text-sm font-medium text-gray-700">{t('plans.form.active_label')}</span>
            </label>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white z-10">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors disabled:opacity-60"
          >
            {t('plans.form.cancel')}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-xl cursor-pointer transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : null}
            {saving ? t('plans.form.saving') : t('plans.form.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
